import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import VideocamIcon from '@mui/icons-material/Videocam';

import UploadOrRecordSelector from '@src/components/CampaignLanding/UploadOrRecordSelector';
import VideoPreview from '@src/components/CampaignLanding/VideoPreview';
import CameraRecord from '@src/components/CameraRecord/CameraRecord';
import UploadError from '@src/components/CampaignLanding/UploadError';
import { RecordNotSupportedMessage } from '@src/components';
import {
  useCreateMediaSubmission,
  usePatchMediaSubmission
} from '@src/hooks/react-query/useMediaSubmissionData';
import { Uploader } from '@src/utils/upload';
import config from '@src/config';
import mediaSubmissionStatusOptions from '@src/constants/media-submissions-status';
import {
  setQuestionVideoUploadSuccess,
  setQuestionVideoUploadError,
  setIsUploadingVideo,
  getSelectedVideoFile,
  getIsUploadingVideo,
  setMediaSubmissionError,
  getIsRetryingUpload,
  getCampaignLanguage
} from '@src/redux/modules/campaignLandingPage';
import { getTranslation } from '@src/translations/dictionary';
import styles from '@src/components/CampaignLanding/VideoCollector.module.less';

const { UPLOAD_S3_START, UPLOAD_S3_SUCCESS, UPLOAD_S3_ERROR } = mediaSubmissionStatusOptions;

const VideoCollector = ({
  qid,
  required = true,
  label = 'Record or Upload Video',
  description = '',
  _setIsUploadingVideo,
  _setQuestionVideoUploadError,
  _setQuestionVideoUploadSuccess,
  _setMediaSubmissionError,
  file,
  isUploadingVideo,
  isRetryingUpload,
  language
}) => {
  const {
    isSuccess: createMediaSubmissionSuccess,
    error: createMediaSubmissionError,
    isError: isCreateMediaSubmissionError,
    data: createMediaSubmissionData,
    mutate: createMediaSubmissionMutation
  } = useCreateMediaSubmission();

  const { mutate: patchMediaSubmissionMutation } = usePatchMediaSubmission();

  const [progress, setProgress] = React.useState(0);

  const attemptMultipartVideoUpload = () => {
    if (!createMediaSubmissionData) {
      return;
    }
    _setIsUploadingVideo(qid, true);
    const { fileKey, mediaSubmissionId } = createMediaSubmissionData;
    patchMediaSubmissionMutation({
      mediaSubmissionId: mediaSubmissionId,
      payload: {
        newStatus: UPLOAD_S3_START
      }
    });

    const uploaderOptions = {
      file,
      baseURL: config.AWS.fileUploadApiGateway,
      chunkSize: 5,
      threadsQuantity: 5,
      useTransferAcceleration: true
    };
    const uploader = new Uploader(uploaderOptions);
    let percentage = undefined;
    uploader
      .onProgress(({ percentage: newPercentage }) => {
        if (newPercentage !== percentage) {
          percentage = newPercentage;
          setProgress(percentage);
        }
      })
      .onError((error) => {
        patchMediaSubmissionMutation({
          mediaSubmissionId: mediaSubmissionId,
          payload: { newStatus: UPLOAD_S3_ERROR, details: error.message }
        });
        _setQuestionVideoUploadError(qid, error);
        _setIsUploadingVideo(qid, false);
      })
      .onComplete(() => {
        patchMediaSubmissionMutation({
          mediaSubmissionId: mediaSubmissionId,
          payload: { newStatus: UPLOAD_S3_SUCCESS }
        });
        _setQuestionVideoUploadSuccess(qid, mediaSubmissionId);
        _setIsUploadingVideo(qid, false);
        setProgress(0);
      });

    uploader.start(fileKey);
  };

  useEffect(() => {
    if (createMediaSubmissionSuccess) {
      attemptMultipartVideoUpload();
    }
  }, [createMediaSubmissionSuccess]);

  useEffect(() => {
    if (isRetryingUpload) {
      attemptMultipartVideoUpload();
    }
  }, [isRetryingUpload]);

  React.useEffect(() => {
    if (isCreateMediaSubmissionError) {
      // Extract error message from axios error structure
      const errorMessage = createMediaSubmissionError?.response?.data?.message
        || createMediaSubmissionError?.message
        || 'Failed to create media submission';
      _setMediaSubmissionError(qid, errorMessage);
    }
  }, [isCreateMediaSubmissionError]);

  const progressBarLabel = getTranslation('upload_in_progess', language);

  return (
    <Box className={styles.vpVideoCollectorContainer}>
      <Box className={styles.vpHeader}>
        <VideocamIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={600} className={styles.vpLabel}>
          {label}
          {required && <span className={styles.vpRequiredLabel}>*</span>}
        </Typography>
      </Box>

      {description && (
        <section className={styles.vpDescription}>{description}</section>
      )}

      <Box className={styles.vpVideoPanel}>
        <VideoPreview qid={qid} createMediaSubmissionFn={createMediaSubmissionMutation} />
        <CameraRecord qid={qid} urlType="clpVideoUpload" />
        <RecordNotSupportedMessage />
        <UploadOrRecordSelector qid={qid} createMediaSubmissionFn={createMediaSubmissionMutation} />
        <UploadError qid={qid} />

        {isUploadingVideo && (
          <Box className={styles.vpProgressBar}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography sx={{ fontSize: '14px', textAlign: 'center', mt: '5px' }}>
              {progressBarLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

VideoCollector.propTypes = {
  qid: PropTypes.string.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  file: PropTypes.any,
  language: PropTypes.string,
  isUploadingVideo: PropTypes.bool,
  isRetryingUpload: PropTypes.bool,
  _setIsUploadingVideo: PropTypes.func,
  _setQuestionVideoUploadError: PropTypes.func,
  _setQuestionVideoUploadSuccess: PropTypes.func,
  _setMediaSubmissionError: PropTypes.func
};

function mapStateToProps(state, ownProps) {
  return {
    file: getSelectedVideoFile(state, ownProps.qid),
    isUploadingVideo: getIsUploadingVideo(state, ownProps.qid),
    isRetryingUpload: getIsRetryingUpload(state, ownProps.qid),
    language: getCampaignLanguage(state)
  };
}

const mapDispatchToProps = {
  _setIsUploadingVideo: setIsUploadingVideo,
  _setQuestionVideoUploadError: setQuestionVideoUploadError,
  _setQuestionVideoUploadSuccess: setQuestionVideoUploadSuccess,
  _setMediaSubmissionError: setMediaSubmissionError
};

export default connect(mapStateToProps, mapDispatchToProps)(VideoCollector);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/VideoCollector.jsx